import React, { useMemo } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const GroupROIRankings = ({ roiData, loading }) => {
  const { currentLanguage, t } = useLanguage();

  const rankedGroups = useMemo(() => {
    if (!roiData?.groups || roiData?.groups?.length === 0) return [];

    return roiData?.groups
      ?.map(group => {
        const revenue = parseFloat(group?.revenue || 0);
        const costs = parseFloat(group?.costs || 0);
        const profit = parseFloat(group?.profit || 0);
        const roi = costs > 0 ? ((profit / costs) * 100) : 0;
        const margin = revenue > 0 ? ((profit / revenue) * 100) : 0;

        return {
          ...group,
          revenue,
          costs,
          profit,
          roi,
          margin
        };
      })
      ?.sort((a, b) => b?.roi - a?.roi);
  }, [roiData]);

  const chartData = useMemo(() => {
    return rankedGroups?.slice(0, 10)?.map(group => ({
      name: group?.name?.substring(0, 15) || 'N/A',
      revenue: group?.revenue,
      costs: group?.costs,
      profit: group?.profit,
      roi: group?.roi
    }));
  }, [rankedGroups]);

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })?.format(amount);
    }
  };

  const getROIColor = (roi) => {
    if (roi >= 30) return '#10b981'; // green
    if (roi >= 15) return '#3b82f6'; // blue
    if (roi >= 5) return '#f59e0b'; // orange
    return '#ef4444'; // red
  };

  const getROIBadge = (roi) => {
    if (roi >= 30) return { label: t('financial.excellent'), color: 'bg-success text-white' };
    if (roi >= 15) return { label: t('financial.good'), color: 'bg-primary text-white' };
    if (roi >= 5) return { label: t('financial.average'), color: 'bg-warning text-white' };
    return { label: t('financial.poor'), color: 'bg-error text-white' };
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-warm-md">
          <p className="text-sm font-medium text-foreground mb-2">
            {payload?.[0]?.payload?.name}
          </p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm font-semibold data-text" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.dataKey === 'roi' 
                ? `${entry?.value?.toFixed(1)}%` 
                : formatCurrency(entry?.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-warm p-6 animate-pulse">
        <div className="h-6 bg-muted rounded w-48 mb-6"></div>
        <div className="space-y-4">
          <div className="h-20 bg-muted rounded"></div>
          <div className="h-20 bg-muted rounded"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">
        {t('financial.roiRankings')}
      </h3>
      {/* Top 3 Groups */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {rankedGroups?.slice(0, 3)?.map((group, index) => {
          const badge = getROIBadge(group?.roi);
          const medals = ['🥇', '🥈', '🥉'];
          
          return (
            <div key={group?.id} className="bg-gradient-to-br from-primary/5 to-success/5 rounded-lg p-4 border-2 border-primary/20">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-2xl">{medals?.[index]}</span>
                    <span className={`caption px-2 py-1 rounded ${badge?.color}`}>
                      {badge?.label}
                    </span>
                  </div>
                  <h4 className="font-semibold text-foreground break-words">{group?.name}</h4>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xl md:text-2xl font-bold text-primary data-text">
                    {group?.roi?.toFixed(1)}%
                  </p>
                  <p className="caption text-muted-foreground">
                    {currentLanguage === 'fr' ? 'ROI' : 'العائد'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="caption text-muted-foreground text-xs">
                    {t('financial.revenues')}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-success data-text break-words">
                    {formatCurrency(group?.revenue)}
                  </p>
                </div>
                <div>
                  <p className="caption text-muted-foreground text-xs">
                    {t('financial.costs')}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-error data-text break-words">
                    {formatCurrency(group?.costs)}
                  </p>
                </div>
                <div>
                  <p className="caption text-muted-foreground text-xs">
                    {t('financial.profit')}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-primary data-text break-words">
                    {formatCurrency(group?.profit)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Cost-Revenue Comparison Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-muted-foreground mb-4">
          {t('financial.revenueVsCosts')}
        </h4>
        {chartData?.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[500px]">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '10px' }}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `${(value / 1000)?.toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="revenue" 
                    fill="#10b981" 
                    name={t('financial.revenues')}
                    radius={[8, 8, 0, 0]}
                  />
                  <Bar 
                    dataKey="costs" 
                    fill="#ef4444" 
                    name={t('financial.costs')}
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 bg-muted/50 rounded-lg">
            <p className="text-muted-foreground">
              {t('financial.noDataAvailable')}
            </p>
          </div>
        )}
      </div>
      {/* Full Rankings Table */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-4">
          {t('financial.fullRanking')}
        </h4>
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden">
              <table className="min-w-full w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-2 caption text-muted-foreground">
                      {t('financial.rank')}
                    </th>
                    <th className="text-left p-2 caption text-muted-foreground">
                      {t('financial.group')}
                    </th>
                    <th className="text-right p-2 caption text-muted-foreground">
                      {currentLanguage === 'fr' ? 'ROI' : 'العائد'}
                    </th>
                    <th className="text-right p-2 caption text-muted-foreground">
                      {t('financial.margin')}
                    </th>
                    <th className="text-right p-2 caption text-muted-foreground">
                      {t('financial.profit')}
                    </th>
                    <th className="text-center p-2 caption text-muted-foreground">
                      {t('financial.performance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rankedGroups?.map((group, index) => {
                    const badge = getROIBadge(group?.roi);
                    return (
                      <tr key={group?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                        <td className="p-2">
                          <span className="font-semibold text-foreground">#{index + 1}</span>
                        </td>
                        <td className="p-2">
                          <span className="text-sm text-foreground">{group?.name}</span>
                        </td>
                        <td className="p-2 text-right">
                          <span className="font-semibold data-text" style={{ color: getROIColor(group?.roi) }}>
                            {group?.roi?.toFixed(1)}%
                          </span>
                        </td>
                        <td className="p-2 text-right">
                          <span className="text-sm text-foreground data-text">
                            {group?.margin?.toFixed(1)}%
                          </span>
                        </td>
                        <td className="p-2 text-right">
                          <span className="text-sm font-semibold text-success data-text">
                            {formatCurrency(group?.profit)}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className={`caption px-2 py-1 rounded ${badge?.color}`}>
                            {badge?.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupROIRankings;